/** Public acquisition preferences. Never grants access or purchases a plan. */
export const audienceIds = ["aspiring-pilot", "professional-pilot"] as const;
export const industryIds = ["construction", "real-estate", "utilities", "energy-and-mining", "telecom", "agriculture", "insurance", "public-safety", "surveying-and-mapping", "filming-and-media", "drone-delivery-and-logistics"] as const;
export const taskRoutes = { profile: "my-profile", schedule: "my-schedule", fleet: "fleet-manage", regulations: "skyrules" } as const;
const memberships = ["community", "pilot_operator", "pilot_professional", "drone_business", "enterprise"];
export type AcquisitionContext = { audience?: typeof audienceIds[number]; industry?: typeof industryIds[number]; task?: keyof typeof taskRoutes; membership?: string };
export const acquisitionStorageKey = "dronejob:acquisitionContext:v1";
export function parseAcquisitionContext(query: URLSearchParams): AcquisitionContext {
  const result: AcquisitionContext = {};
  const audience = query.get("audience"); const industry = query.get("industry"); const task = query.get("task"); const membership = query.get("membership");
  if (audienceIds.includes(audience as never)) result.audience = audience as AcquisitionContext["audience"];
  if (industryIds.includes(industry as never)) result.industry = industry as AcquisitionContext["industry"];
  if (task && Object.prototype.hasOwnProperty.call(taskRoutes, task)) result.task = task as AcquisitionContext["task"];
  if (membership && memberships.includes(membership)) result.membership = membership;
  return result;
}
export function contextQuery(context: AcquisitionContext): string {
  const q = new URLSearchParams();
  for (const [key,value] of Object.entries(context)) if (value) q.set(key,value);
  return new URLSearchParams(parseAcquisitionContext(q) as Record<string,string>).toString();
}
export function contextWorkspaceHash(locale: string, context: AcquisitionContext): string {
  const language = ["en", "es", "pt-BR"].includes(locale) ? locale : "en";
  return `#/${language}/${taskRoutes[context.task ?? "profile"]}?${contextQuery(context)}`;
}
/** Reject arbitrary return routes, nested wrappers, protocols, and unknown contexts. */
export function safeContextReturnHash(value?: string | null): string | null {
  if (!value || !/^#\/(en|es|pt-BR)\/(my-profile|my-schedule|team-schedule|fleet-manage|skyrules)\?/.test(value)) return null;
  const [path,query] = value.split("?"); const context = parseAcquisitionContext(new URLSearchParams(query));
  if (!context.audience && !context.industry && !context.membership && !context.task) return null;
  const normalized = path.endsWith("/team-schedule") && context.task === "schedule"
    ? `${path}?${contextQuery(context)}`
    : contextWorkspaceHash(path.split("/")[1], context);
  return normalized.split("?")[0] === path ? normalized : null;
}
export function contextFromHash(hash: string): AcquisitionContext {
  const query = new URLSearchParams(hash.split("?")[1] ?? "");
  const target = safeContextReturnHash(query.get("returnTo"));
  return parseAcquisitionContext(target ? new URLSearchParams(target.split("?")[1]) : query);
}
export function saveAcquisitionContext(context: AcquisitionContext): void {
  if (typeof window !== "undefined") window.sessionStorage.setItem(acquisitionStorageKey, contextQuery(context));
}
export function readAcquisitionContext(): AcquisitionContext {
  if (typeof window === "undefined") return {};
  const incoming = contextFromHash(window.location.hash);
  if (Object.keys(incoming).length) { saveAcquisitionContext(incoming); return incoming; }
  return parseAcquisitionContext(new URLSearchParams(window.sessionStorage.getItem(acquisitionStorageKey) ?? ""));
}

/** Update the public hash as well as transient storage so edits survive refresh/back. */
export function updateAcquisitionSelection(locale: string, context: AcquisitionContext): string {
 const target = contextWorkspaceHash(locale, context);
 saveAcquisitionContext(context);
 if (typeof window !== "undefined") {
  const [path,query] = window.location.hash.split("?");
  if (/^#\/(en|es|pt-BR)\/(signup|login)$/.test(path)) {
   const params = new URLSearchParams(query ?? ""); params.set("returnTo", target);
   window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}${path}?${params.toString()}`);
  } else if (/^#\/(en|es|pt-BR)\/(my-profile|my-schedule|team-schedule|fleet-manage|skyrules)$/.test(path)) {
   window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}${path}?${contextQuery(context)}`);
  }
 }
 return target;
}
